import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Users, Eye, Target, Play, TrendingUp } from 'lucide-react';

const useCounter = (end, duration = 1200) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

const MetricCard = ({ icon: Icon, iconColor, label, value, suffix = '', from, to, delta }) => {
    const [animatedValue, ref] = useCounter(value);

    return (
        <GlassCard>
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-3xl font-black text-white tracking-tight mb-3">
                    {suffix === 'K' ? `${(animatedValue / 1000).toFixed(animatedValue >= 1000 ? 0 : 1)}K` : animatedValue.toLocaleString()}
                </p>
                <div className="flex items-center justify-between text-xs">
                    <span className="text-white/40">{from} → {to}</span>
                    <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 font-bold px-2 py-0.5 rounded-full">
                        <TrendingUp size={10} />
                        {delta}
                    </span>
                </div>
            </div>
        </GlassCard>
    );
};

const GrowthProgressBar = () => {
    const [width, setWidth] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    setTimeout(() => setWidth(100), 200);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className="mt-8">
            <GlassCard>
                <div className="flex items-center justify-between mb-4">
                    <span className="text-white/60 text-sm font-semibold">Follower Growth</span>
                    <span className="text-white font-mono text-sm">676 → 2,160</span>
                </div>
                <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden relative">
                    <div
                        className="h-full rounded-full transition-all duration-[2000ms] ease-out relative overflow-hidden"
                        style={{
                            width: `${width}%`,
                            background: 'linear-gradient(90deg, #7c3aed, #a78bfa, #7c3aed)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 2s infinite linear',
                        }}
                    />
                </div>
                <div className="flex justify-between mt-2">
                    <span className="text-white/30 text-xs">Feb 24</span>
                    <span className="text-purple-400 text-xs font-semibold">+220% Growth</span>
                    <span className="text-white/30 text-xs">Mar 24</span>
                </div>
            </GlassCard>
            <style>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
        </div>
    );
};

export const GrowthBreakdown = () => {
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl">
                <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">Growth Breakdown</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">
                    The Numbers Don't Lie
                </h2>
                <p className="text-white/50 text-center mb-12 max-w-xl mx-auto">
                    Every key metric saw significant improvement in just 30 days.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <MetricCard
                        icon={Users}
                        iconColor="bg-purple-500/20 text-purple-300"
                        label="Followers Gained"
                        value={1484}
                        from="676"
                        to="2,160"
                        delta="+220%"
                    />
                    <MetricCard
                        icon={Eye}
                        iconColor="bg-blue-500/20 text-blue-300"
                        label="Total Views"
                        value={830095}
                        from="~15K"
                        to="830K+"
                        delta="55×"
                    />
                    <MetricCard
                        icon={Target}
                        iconColor="bg-emerald-500/20 text-emerald-300"
                        label="Avg. Reach"
                        value={16265}
                        from="~6,402"
                        to="16,265"
                        delta="2.5×"
                    />
                    <MetricCard
                        icon={Play}
                        iconColor="bg-amber-500/20 text-amber-300"
                        label="Top Reel Views"
                        value={142000}
                        suffix="K"
                        from="0"
                        to="142K"
                        delta="#1 Reel"
                    />
                </div>

                <GrowthProgressBar />
            </div>
        </section>
    );
};
